import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { getRenterQualificationByTenantScreeningCriteria } from 'common/householdApplication/prequalificationHelpers';

import { hasRoleAnonymous } from 'common/userRoles';

import SrCardOld from 'client/components/SrCardOld';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrEmoji from 'client/components/SrEmoji';
import SrSeparator from 'client/components/SrSeparator';
import SrTooltip, {
  SR_TOOLTIP_POSITION_LEFT,
  SR_TOOLTIP_ICON_QUESTION_MARK,
} from 'client/components/SrTooltip';
import ButtonDeprecated, { BUTTON_THEME_YELLOW } from 'client/components/ButtonDeprecated';
import SrApplicationButton from 'client/components/SrApplicationButton';
import SrRentersApplyingToListingAlert from 'client/components/SrRentersApplyingToListingAlert';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_LEASED,
  LISTING_STATUS_PENDING,
  LISTING_STATUS_NOT_AVAILABLE,
  LISTING_STATUS_NEEDS_REVIEW,
} from 'common/listingConstants';

import { TENANT_SCREENING_CRITERIA_CREDIT_SCORE_QUALIFICATION_AVERAGE } from 'common/tenantScreeningCriteriaConstants';

import log from 'client/lib/log';
import HouseholdActions from 'client/actions/Household';

class SrPrequalCard extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    partner: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    renterProfile: PropTypes.object,
    tenantScreeningCriteria: PropTypes.object,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    householdsApplyingToListing: PropTypes.object,
  };

  _onStartPrequalButtonClick = () => {
    const { currentUser, listing, history, dispatch } = this.props;

    if (hasRoleAnonymous(currentUser.roles)) {
      history.push(`/login?startPrequal=true&listingId=${listing.id}`);
      return;
    }

    dispatch(HouseholdActions.startPrequal, {
      userId: currentUser.id,
      listingId: listing.id,
    }).catch((error) => {
      log.error(error);
    });
  };

  _handleNoPrequalButtonDisplay = ({ listing, isQualified, isPrequalCompleted }) => {
    const takeQuizComponent = <span className="buttonContent">apply now</span>;

    switch (listing.status) {
      case LISTING_STATUS_LEASED:
      case LISTING_STATUS_PENDING:
        return { buttonDisabled: true, buttonContent: 'leased' };
      case LISTING_STATUS_NOT_AVAILABLE:
      case LISTING_STATUS_NEEDS_REVIEW:
        return { buttonDisabled: true, buttonContent: 'not available' };
      default:
        if (!isPrequalCompleted) {
          return { buttonDisabled: false, buttonContent: takeQuizComponent };
        }
        if (!isQualified) {
          return { buttonDisabled: true, buttonContent: 'not qualified' };
        }
        return { buttonDisabled: false, buttonContent: takeQuizComponent };
    }
  };

  render() {
    const {
      listing,
      partner,
      currentUser,
      renterProfile,
      tenantScreeningCriteria,
      householdsApplyingToListing,
    } = this.props;

    const { matches, isQualified, requirements } = getRenterQualificationByTenantScreeningCriteria({
      listing,
      tenantScreeningCriteria,
      householdApplication: renterProfile || {},
      excludeCreditScoreQualification:
        tenantScreeningCriteria.creditScoreQualification ===
        TENANT_SCREENING_CRITERIA_CREDIT_SCORE_QUALIFICATION_AVERAGE, // eslint-disable-line max-len
    });

    const isPrequalCompleted = renterProfile && renterProfile.isPrequalCompleted;

    const { buttonDisabled, buttonContent } = this._handleNoPrequalButtonDisplay({
      listing,
      isQualified,
      isPrequalCompleted,
    });

    return (
      <SrCardOld className="SrPrequalCard">
        <div className="headerWrapper">
          {!isPrequalCompleted && (
            <SrText size="2xl" weight="bold" leading="2xl" color="green">
              {hasRoleAnonymous(currentUser.roles)
                ? 'Do you want to apply? Start here, take the quiz.'
                : 'Take the 2 minute quiz to check if you’re qualified to rent this home.'}
            </SrText>
          )}
          {isPrequalCompleted && (
            <SrFlex justify="between" className="results">
              <div>
                <SrText
                  size="2xl"
                  weight="bold"
                  color={isQualified ? 'green' : 'orange300'}
                  display="block"
                >
                  {matches}/{requirements.length} Match{isQualified ? '!' : ''}
                </SrText>
                <div style={{ height: 4 }} />
                <SrText font="whitney-sc" weight="bold" color={isQualified ? 'green' : 'blue'}>
                  qualified to rent this home
                </SrText>
              </div>
              <SrEmoji emoji={isQualified ? '👌' : '🤔'} big className="prequalEmoji" />
            </SrFlex>
          )}
        </div>
        <SrSeparator />
        <div className="contentWrapper">
          {requirements.map((requirement) => (
            <div key={requirement.title} className="row">
              <div>
                <div
                  className={classnames('icon', {
                    qualified: isPrequalCompleted && requirement.isQualified,
                    notQualified: isPrequalCompleted && !requirement.isQualified,
                  })}
                />
              </div>
              <SrText
                color={isPrequalCompleted && !requirement.isQualified ? 'red' : 'blue'}
                weight={isPrequalCompleted && !requirement.isQualified ? 'bold' : 'medium'}
              >
                {requirement.title}
              </SrText>
              {requirement.tooltipCopy && (
                <SrBox paddingLeft="5px">
                  <SrTooltip
                    iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
                    position={SR_TOOLTIP_POSITION_LEFT}
                  >
                    {requirement.tooltipCopy}
                  </SrTooltip>
                </SrBox>
              )}
            </div>
          ))}
          <div className="row">
            <SrText color="blue" weight="500" paddingLeft="24px" fontStyle="italic">
              Not all types of felonies are accepted
            </SrText>
          </div>
          <div style={{ height: 13 }} />
          {isPrequalCompleted && isQualified && (
            <SrApplicationButton listing={listing} partner={partner} />
          )}
          {(!isPrequalCompleted || !isQualified) && (
            <ButtonDeprecated
              theme={BUTTON_THEME_YELLOW}
              disabled={(isPrequalCompleted && !isQualified) || buttonDisabled}
              onClick={this._onStartPrequalButtonClick}
            >
              {buttonContent}
            </ButtonDeprecated>
          )}
          {isPrequalCompleted && !isQualified && listing.status === LISTING_STATUS_ACTIVE && (
            <div className="t-centered">
              <div style={{ height: 8 }} />
              <SrText weight="medium" size="medium">
                You must match all qualifications to apply.
              </SrText>
            </div>
          )}
        </div>
        {listing.status === LISTING_STATUS_ACTIVE && (
          <SrBox marginTop={-5}>
            <SrRentersApplyingToListingAlert
              householdsApplyingToListing={householdsApplyingToListing}
              isExternalApp={!!partner?.experienceConfiguration?.externalApplicationEnabled}
              listing={listing}
            />
          </SrBox>
        )}
      </SrCardOld>
    );
  }
}

SrPrequalCard.displayName = 'SrPrequalCard';

export default SrPrequalCard;
